import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GetUser, UserUpdateEmail, UserUpdateVerification } from '../../services/profile';

const EditEmail = () => {
  const [email, setEmail] = useState('');
  const [hasCode, setHasCode] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await GetUser();
      if (response.statusCode === 200) {
        setEmail(response.data.email);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('editEmail.error'));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleUpdateEmail = async () => {
    if (!validateEmail(email)) {
      toast.error(t('editEmail.invalidEmail'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await UserUpdateEmail(email);
      if (response.statusCode === 200) {
        toast.success(response.message);
        setHasCode(true);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('editEmail.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerification = async () => {
    if (!activationCode || activationCode.length !== 6) {
      toast.error(t('editEmail.invalidCode'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await UserUpdateVerification(activationCode);
      if (response.statusCode === 200) {
        toast.success(response.message);
        navigate(-1);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('editEmail.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        px: 2,
        background: theme.palette.background.default
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={theme.palette.mode === 'dark' ? 3 : 1}
          sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
        >
          <Typography variant="h5" component="h1" align="center" gutterBottom>
            {t('editEmail.title')}
          </Typography>

          <Box sx={{ mt: 3 }}>
            {!hasCode ? (
              <TextField
                margin="normal"
                required
                fullWidth
                label={t('editEmail.emailLabel')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('editEmail.emailPlaceholder')}
              />
            ) : (
              <>
                <Typography align="center" sx={{ mb: 2 }}>
                  {t('editEmail.codeInstruction')}
                </Typography>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={t('editEmail.codeLabel')}
                  value={activationCode}
                  onChange={(e) => setActivationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*'
                  }}
                  placeholder={t('editEmail.codePlaceholder')}
                />
              </>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => hasCode ? setHasCode(false) : navigate(-1)}
                disabled={isLoading}
              >
                {t('common.back')}
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={hasCode ? handleVerification : handleUpdateEmail}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  t('common.submit')
                )}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default EditEmail; 